import { CellEditingStartedEvent, CellValueChangedEvent, GridApi, IRowNode } from 'ag-grid-community';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useState } from 'react';
import { CompanyMappingStatusUI, CompanyMappingVM } from '../../../schemas/CompanyMappings.schema';
import { highlightRow } from '../../../util/ag-grid-utils';

const GRID_MUTATION_EVENTS = ['cellValueChanged', 'rowDataUpdated', 'modelUpdated'] as const;

function onGridMutation(gridApi: GridApi, recompute: () => void): () => void {
  for (const evt of GRID_MUTATION_EVENTS) {
    gridApi.addEventListener(evt, recompute);
  }
  return () => {
    for (const evt of GRID_MUTATION_EVENTS) {
      gridApi.removeEventListener(evt, recompute);
    }
  };
}

const terminalStatuses = new Set<string>([CompanyMappingStatusUI.mapped, CompanyMappingStatusUI.skipped]);

export function isSelectableMapping(data: CompanyMappingVM | undefined): boolean {
  if (!data) {
    return false;
  }
  if (data.modified) {
    return Boolean(data.company?.fields?.name);
  }
  if (terminalStatuses.has(data.status)) {
    return false;
  }
  return Boolean(data.company?.fields?.name);
}

export function isModifiedMapping(data: CompanyMappingVM | undefined): boolean {
  return Boolean(data?.modified && data.company?.fields?.name);
}

export const mappingsGridApiAtom = atom<GridApi | null>(null);

export const selectedMappingsRowNodeState = atom<IRowNode<CompanyMappingVM> | null>(null);

export const showCreateCompanyDialogState = atom<boolean>(false);

export function useMappingsGridEditState() {
  const setSelectedMappingsRowNode = useSetAtom(selectedMappingsRowNodeState);

  const onCellEditingStarted = useCallback(
    (params: CellEditingStartedEvent<CompanyMappingVM>) => {
      const rowNode = params.node;
      if (!rowNode) {
        return;
      }
      setSelectedMappingsRowNode(rowNode);
    },
    [setSelectedMappingsRowNode]
  );

  const onCellValueChanged = useCallback((params: CellValueChangedEvent<CompanyMappingVM>) => {
    if (params.column.getColDef()?.field !== 'company') {
      return;
    }
    params.api?.applyTransaction({
      update: [
        {
          ...params.node.data,
          modified: Boolean(params.node.data?.company?.fields?.name),
        } as CompanyMappingVM,
      ],
    });
    highlightRow(params);
  }, []);

  return { onCellEditingStarted, onCellValueChanged };
}

export function useModifiedMappingsCount(): number {
  const gridApi = useAtomValue(mappingsGridApiAtom);
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!gridApi) {
      setCount(0);
      return;
    }
    const recompute = () => {
      let next = 0;
      gridApi.forEachNode((node) => {
        if (isModifiedMapping(node.data)) {
          next += 1;
        }
      });
      setCount(next);
    };
    recompute();
    return onGridMutation(gridApi, recompute);
  }, [gridApi]);
  return count;
}

export function collectModifiedMappings(gridApi: GridApi | null): CompanyMappingVM[] {
  if (!gridApi) {
    return [];
  }
  const rows: CompanyMappingVM[] = [];
  gridApi.forEachNode((node) => {
    if (isModifiedMapping(node.data)) {
      rows.push(node.data as CompanyMappingVM);
    }
  });
  return rows;
}

export function useHasDirtyMappings(): boolean {
  const gridApi = useAtomValue(mappingsGridApiAtom);
  const [hasDirty, setHasDirty] = useState(false);
  useEffect(() => {
    if (!gridApi) {
      setHasDirty(false);
      return;
    }
    const recompute = () => {
      let dirty = false;
      gridApi.forEachNode((node) => {
        if (node.data?.modified) {
          dirty = true;
        }
      });
      setHasDirty(dirty);
    };
    recompute();
    return onGridMutation(gridApi, recompute);
  }, [gridApi]);
  return hasDirty;
}
